import React from 'react';
import PropTypes from 'prop-types';
import './IndicatorTable.css';
import ExpandableTableRow from 'components/ExpandableTableRow/ExpandableTableRow';
import ChartContainer from 'components/Chart/ChartContainer';
import { formatPeriod, formatValue } from 'lib/Utils';
import { MobileDeviceContext } from 'lib/Screen';
import translations from 'data/translations.json';

class IndicatorTable extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            dataComplete: new Array(this.props.data.length).fill(null)
        }

        this.loadData = this.loadData.bind(this);
        this.collapseRowsExceptOne = this.collapseRowsExceptOne.bind(this);

        this.expandedGraphRows = this.props.data.map(_ => React.createRef());

        this.numberFormat = new Intl.NumberFormat(window.lang === "de" ? "de-DE" : "it-IT");
    }

    loadData() {
        import(`data/${this.props.code}Complete.json`).then(({ default: jsonData }) => {
            //console.log("Data complete:", jsonData);
            this.setState({ dataComplete: jsonData.data });
        });
    }

    collapseRowsExceptOne(expandingRow) {
        for (const row of this.expandedGraphRows) {
            if (expandingRow !== row) {
                row.current.collapse();
            }
        }
    }

    appendSpan(value, fractionDigits, lang, translations) {
        return <React.Fragment> {formatValue(value, fractionDigits, lang, translations)} <span className='ct-c' title={translations["provisional"][window.lang]}>p</span> </React.Fragment>;
    }

    renderDesktopTable() {
        let columnCount = 3;

        return (
            <table className="indicator-table">
                <thead>
                    <tr key={"ith0"}>
                        <th rowSpan={2} key={"ith0-0"}>{translations.content[window.lang]}</th>
                        {
                            this.props.schema.map((schema, i) => {
                                const key = Object.getOwnPropertyNames(schema)[0];
                                const data = schema[key];
                                if (data.length === 0) {
                                    return (<th rowSpan={2} key={`ith0-${i + 1}`}>{translations[key][window.lang]}</th>)
                                } else if (data.length > 1) {
                                    return (<th colSpan={data.length} key={`ith0-${i + 1}`}>{translations[key][window.lang]}</th>)
                                } else {
                                    return (<th key={`ith0-${i + 1}`}>{translations[key][window.lang]}</th>)
                                }
                            })
                        }
                        <th rowSpan={2}>{translations.graph[window.lang]}</th>
                        <th rowSpan={2}></th>
                    </tr>
                    <tr key={"ith1"}>
                        {
                            this.props.schema.map((schema, i) => {
                                const key = Object.getOwnPropertyNames(schema)[0];
                                columnCount += schema[key].length === 0 ? 1 : schema[key].length;
                                return schema[key].map((field, j) => (<th key={`ith1-${i}-${j}`}>{translations[field][window.lang]}</th>))
                            })
                        }
                    </tr>
                </thead>
                <tbody>
                    {
                        this.props.data.map((dataEntry, i) => {
                            //console.log("loadData= " + this.loadData + " data= " + this.state.dataComplete[i] + " title= " + this.props.title + " source= " + this.props.source);
                            const chart = dataEntry.chart ? (<ChartContainer loadData={this.loadData} data={this.state.dataComplete[i]} title={this.props.title} source={this.props.source} />) : null;
                            return (
                                <ExpandableTableRow expandData={chart} expandColSpan={columnCount} isDummyElement={!dataEntry.chart} onExpand={this.collapseRowsExceptOne}
                                    ref={this.expandedGraphRows[i]} downloadRef={`download/ASTAT_${dataEntry.name.toUpperCase()}_${window.lang.toUpperCase()}.xlsx`} key={`itb${i}`}>
                                    <th key={`itb${i}-0`}>{translations[dataEntry.name][window.lang]}</th>
                                    {
                                        dataEntry.values.map((value, j) => (<td key={`itb${i}-${j + 1}`}>{(dataEntry.provisional == true && j == 0) ? this.appendSpan(value, dataEntry.numDigits[j], window.lang, translations) : formatValue(value, dataEntry.numDigits[j], window.lang, translations)}</td>))
                                    }
                                </ExpandableTableRow>
                            );
                        })
                    }
                </tbody>
                <tfoot>
                    <tr key={"itf"}>
                        <td colSpan={columnCount} key={"itf-0"}>{this.props.source}</td>
                    </tr>
                </tfoot>
            </table>
        );
    }

    renderMobileTable() {
        return (
            <div className="indicator-table-mobile">
                {
                    this.props.data.map((dataEntry, index) => {
                        // Each entry is a row in a desktop table, multiple rows in mobile

                        const chart = dataEntry.chart ? (<ChartContainer loadData={this.loadData} data={this.state.dataComplete[index]} title={this.props.title} source={this.props.source} />) : null

                        let counter = 0;
                        const rows = this.props.schema.flatMap((schema, i) => {
                            const key = Object.getOwnPropertyNames(schema)[0];
                            const columns = schema[key];

                            const rowHeadBase = translations[key][window.lang];
                            const rows = [];

                            if (columns.length > 0) {
                                for (const column of columns) {
                                    const digits = dataEntry.numDigits[counter];
                                    const value = (dataEntry.provisional == true && counter == 0) ? this.appendSpan(dataEntry.values[counter], digits, window.lang, translations) : dataEntry.values[counter];
                                    rows.push((
                                        <tr key={`itb-${counter}`}>
                                            <th>{`${rowHeadBase} - ${translations[column][window.lang]}`}</th>
                                            <td>{typeof value === 'number' ? new Intl.NumberFormat(window.lang === "de" ? "de-DE" : "it-IT", {
                                            minimumFractionDigits: digits, maximumFractionDigits: digits
                                        }).format(value) : formatPeriod(value, window.lang, translations)}</td>
                                        </tr>
                                    ));

                                    counter++;
                                }
                            } else {
                                const digits = dataEntry.numDigits[counter];
                                const value = (dataEntry.provisional == true && counter == 0) ? this.appendSpan(dataEntry.values[counter], digits, window.lang, translations) : dataEntry.values[counter];
								
                                rows.push((
                                    <tr key={`itb-${counter}`}>
                                        <th>{rowHeadBase}</th>
                                        <td>{typeof value === 'number' ? new Intl.NumberFormat(window.lang === "de" ? "de-DE" : "it-IT", {
                                            minimumFractionDigits: digits, maximumFractionDigits: digits
                                        }).format(value) : formatPeriod(value, window.lang, translations)}</td>
                                    </tr>
                                ));

                                counter++;
                            }

                            return rows;
                        });

                        rows.push((
                            <ExpandableTableRow expandData={chart} expandColSpan={2} isDummyElement={!dataEntry.chart} onExpand={this.collapseRowsExceptOne} mobile={true}
                                ref={this.expandedGraphRows[index]} downloadRef={`download/ASTAT_${dataEntry.name.toUpperCase()}_${window.lang.toUpperCase()}.xlsx`} key={`itb-${counter}`} />
                        ));

                        return (
                            <table key={`it-${index}`}>
                                <thead>
                                    <tr key="ith">
                                        <th colSpan="2">{translations[dataEntry.name][window.lang]}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows}
                                </tbody>
                            </table>
                        );
                    })
                }
                <div>
                    <span>{this.props.source}</span>
                </div>
            </div>
        );
    }

    render() {
        const isMobile = this.context;
        return isMobile ? this.renderMobileTable() : this.renderDesktopTable();
    }
}

IndicatorTable.contextType = MobileDeviceContext;

IndicatorTable.propTypes = {
    code: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    schema: PropTypes.arrayOf(PropTypes.object).isRequired,
    source: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        values: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object])).isRequired,
        chart: PropTypes.bool.isRequired
    })).isRequired
};

export default IndicatorTable;